import type { AuthProvider } from '@doss/common-all'
import { config as developmentConfig } from './config.development'
import { config as productionConfig } from './config.production'
import { config as stagingConfig } from './config.staging'

interface FirebaseConfig {
  apiKey: string
  authDomain: string
  databaseURL: string
  projectId: string
  storageBucket: string
  messagingSenderId: string
  appId: string
  measurementId: string
}

interface Config {
  adminBaseUrl: string
  apiBaseUrl: string
  docuforgeBaseUrl: string
  webBaseUrl: string
  webhooksBaseUrl: string
  sentryUrl: string
  logrocketId: string
  protocol: string
  websocketProtocol: string
  firebaseConfig: FirebaseConfig
  jitsuHost?: string
  jitsuWriteKey?: string
  analyticsJitsuEnabled?: boolean
  perfTelemetryEnabled?: boolean
  stripePublishableKey: string
  gcpServiceAccountEmail: string
  stripeStagingPublishableKey: string
  authProvider?: AuthProvider // Authentication provider (defaults to 'firebase')
  loopReturnsInstallUrl: string
  /** CDN prefix the guest frame loads ESM App SDK assets from (`sdk/apps/<sha256>.js|css`). */
  appSdkAssetBase: string
}

const environment = import.meta.env.VITE_ENV || import.meta.env.VITE_VERCEL_ENV
let config: Config = developmentConfig
if (environment === 'production') {
  config = productionConfig
} else if (environment === 'staging' || environment === 'preview') {
  config = stagingConfig
}

const constants = {
  adminWebUrl: `${config.protocol}://${config.adminBaseUrl}`,
  apiUrl: `${config.protocol}://${config.apiBaseUrl}/api/v1`,
  webBaseUrl: `${config.protocol}://${config.webBaseUrl}`,
  webhooksUrl: `${config.protocol}://${config.webhooksBaseUrl}`,
  graphqlApiUrl: `${config.protocol}://${config.apiBaseUrl}/graphql`,
  subscriptionsUrl: `${config.websocketProtocol}://${config.apiBaseUrl}/subscriptions`,
  docuforgeUrl: `${config.protocol}://${config.docuforgeBaseUrl}`,
  sentryDsn: config.sentryUrl,
  logRocketId: config.logrocketId,
  // Build-time define from Doppler; see vite.config.ts.
  muiLicenseKey: __MUI_LICENSE_KEY__,
  supportEmail: 'support@doss.com',
  firebaseConfig: config.firebaseConfig,
  isStaging: environment === 'staging',
  isProduction: environment === 'production',
  jitsuHost: config.jitsuHost,
  jitsuWriteKey: config.jitsuWriteKey,
  analyticsJitsuEnabled: config.analyticsJitsuEnabled ?? false,
  perfTelemetryEnabled: environment !== 'preview' && (config.perfTelemetryEnabled ?? false),
  stripePublishableKey: config.stripePublishableKey,
  stripeStagingPublishableKey: config.stripeStagingPublishableKey,
  onboardingCalendarLink: 'https://calendly.com/doss-com/doss-101',
  copilotKitKey: 'ck_pub_1591dd554b5b30b09f69bc37e10a36ea',
  gcpServiceAccountEmail: config.gcpServiceAccountEmail,
  // Auth provider: Defaults to 'workos', legacy is 'firebase' can be overridden with VITE_AUTH_PROVIDER in Vercel
  authProvider: (config.authProvider ?? 'workos') as AuthProvider,
  loopReturnsInstallUrl: config.loopReturnsInstallUrl,
  appSdkAssetBase: config.appSdkAssetBase,
}

export default constants
